/**
 * RollFeed — the docked, collapsible shared roll feed (dev handoff 2026-08-24,
 * `vtt-dm-roll-visibility`).
 *
 * Mounted ONCE from the app shell so it's visible from every view: battle map,
 * lore, dashboard, sheet. It owns the app-wide SessionLive socket (bound to
 * the campaign session, reconnecting) and shapes every `session/log-entry`
 * roll broadcast into `rollFeedStore`. It never toasts — the roller already
 * gets a toast from useSheetSocket.doRoll, and the DM doesn't want a second
 * notification channel; the collapsed pill carries an unread count instead.
 *
 * "Log a roll" posts an off-app roll (physical dice / D&D Beyond) through the
 * SAME `session/roll` path the sheet uses, so it's audited and broadcast like
 * any other roll — the server stamps `source: manual` so nobody mistakes it.
 *
 * Open by default for the DM, collapsed for players; the choice persists per
 * browser in localStorage.
 *
 * Dev handoff 2026-09-21 (`vtt-feed-noise`): during the hourly-Athletics
 * climb the DM lost track of whose roll was whose. The panel now leads with
 * a "latest per character" strip (one line per character, newest first, the
 * freshest one highlighted as it lands), every roll row is a single scannable
 * line (name · check · total · adv/dis), and an All / Rolls filter hides the
 * session-lifecycle events that ride the same log channel.
 */
import './RollFeed.css';
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { Badge, Icon, IconButton } from './ui';
import { useAuth } from '../lib/AuthContext';
import { isAdmin } from '../lib/auth';
import { useIdentity } from '../lib/ImpersonationContext';
import { getSessionLive, type SessionLiveStatus } from '../lib/sessionLive';
import { DEFAULT_SESSION_ID, getCharacters, type CharacterBundle } from '../lib/sessionApi';
import { SessionServerTypes, type LogEntryPayload } from '../lib/sessionProtocol';
import { ConvergenceServerTypes } from '../lib/convergenceProtocol';
import {
  MANUAL_ROLL_TYPES,
  buildManualRollRequest,
  filterFeedEntries,
  formatRollLine,
  isFeedFilter,
  latestPerCharacter,
  rollFeedStore,
  rollLine,
  toFeedEntry,
  type FeedFilter,
  type RollFeedRollEntry,
  type RollFeedSystemEntry,
} from '../lib/rollFeed';
import { reportFrontendError } from '../lib/errorCapture';

// Same default the Sheet / Dashboard / VTT use for the subscribe handshake.
const DEFAULT_MAP_ID = 'local-dev';
const OPEN_KEY = 'gd:rollfeed-open';
const FILTER_KEY = 'gd:rollfeed-filter';

function readOpenPref(): boolean | null {
  try {
    const v = localStorage.getItem(OPEN_KEY);
    return v === '1' ? true : v === '0' ? false : null;
  } catch {
    return null;
  }
}

function writeOpenPref(open: boolean): void {
  try {
    localStorage.setItem(OPEN_KEY, open ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/** Per-viewer feed filter; "all" until they choose otherwise. */
function readFilterPref(): FeedFilter {
  try {
    const v = localStorage.getItem(FILTER_KEY);
    return isFeedFilter(v) ? v : 'all';
  } catch {
    return 'all';
  }
}

function writeFilterPref(filter: FeedFilter): void {
  try {
    localStorage.setItem(FILTER_KEY, filter);
  } catch {
    /* ignore */
  }
}

function formatTime(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDice(e: RollFeedRollEntry): string {
  if (e.expression === 'manual') return 'total only';
  const dice = `[${e.results.join(', ')}]`;
  const mod = e.modifier !== 0 ? ` ${e.modifier > 0 ? '+' : '−'} ${Math.abs(e.modifier)}` : '';
  return `${e.expression} → ${dice}${mod}`;
}

function entryStateClass(e: RollFeedRollEntry): string {
  return (e.natural ? ` rollfeed__row--${e.natural}` : '') + (e.whisper ? ' rollfeed__row--whisper' : '');
}

/**
 * The one line the DM scans: name · check · adv/dis · total. Shared by the
 * latest-per-character strip and the full list so they read identically.
 */
function RollLineView({ e }: { e: RollFeedRollEntry }) {
  const line = rollLine(e);
  return (
    <>
      <span className="rollfeed__row-name">{line.name}</span>
      <span className="rollfeed__row-check">{line.check}</span>
      {line.mode && (
        <span
          className={`rollfeed__mode rollfeed__mode--${line.mode.toLowerCase()}`}
          title={line.mode === 'ADV' ? 'Rolled with advantage' : 'Rolled with disadvantage'}
        >
          {line.mode}
        </span>
      )}
      <span className="rollfeed__row-total">{line.total}</span>
    </>
  );
}

function characterLabel(c: CharacterBundle): string {
  return (c.profile as { name?: string } | null)?.name ?? c.id;
}

export default function RollFeed() {
  const { user } = useAuth();
  const { effectiveUser, impersonating } = useIdentity();
  const dm = !impersonating && isAdmin(user);
  // The battle map keeps its drawers on the right edge; dock the feed on the
  // left there so an open Assets/Vault drawer never sits under it.
  const onVtt = useLocation().pathname.startsWith('/vtt');
  const dockClass = onVtt ? ' rollfeed--vtt' : '';

  const snapshot = useSyncExternalStore(
    (cb) => rollFeedStore.subscribe(cb),
    () => rollFeedStore.get(),
    () => rollFeedStore.get(),
  );

  const [open, setOpen] = useState<boolean>(() => readOpenPref() ?? dm);
  const [filter, setFilter] = useState<FeedFilter>(readFilterPref);
  const [status, setStatus] = useState<SessionLiveStatus>('idle');
  const [roster, setRoster] = useState<CharacterBundle[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const rosterRef = useRef<CharacterBundle[]>([]);
  rosterRef.current = roster;

  // First-run default follows the role: the DM wants it open, players don't.
  // Only applies until the user has toggled it once.
  useEffect(() => {
    if (readOpenPref() === null) setOpen(dm);
  }, [dm]);

  // Unread only accrues while collapsed.
  useEffect(() => {
    rollFeedStore.setCountUnread(!open);
  }, [open]);

  // Roster for character-name resolution + the log form's character picker.
  const refreshRoster = useCallback(() => {
    getCharacters(DEFAULT_SESSION_ID)
      .then((list) => setRoster(list.characters))
      .catch(() => {
        /* the feed still works without names */
      });
  }, []);

  useEffect(() => {
    if (!user) return;
    refreshRoster();
  }, [user, refreshRoster]);

  // The one app-wide session-bound socket. Started here, kept alive for the
  // life of the signed-in session (stop on sign-out).
  useEffect(() => {
    if (!user) return;
    const live = getSessionLive({ sessionId: DEFAULT_SESSION_ID, mapId: DEFAULT_MAP_ID });
    const offStatus = live.onStatus(setStatus);
    setStatus(live.status);
    const offLog = live.on<LogEntryPayload>(SessionServerTypes.LogEntry, (payload) => {
      const entry = toFeedEntry(payload, rosterRef.current);
      if (entry) rollFeedStore.push(entry);
    });
    // A ratified merge / new character changes names — refresh the roster.
    const offConv = live.on(ConvergenceServerTypes.Committed, () => refreshRoster());
    void live.start();
    return () => {
      offStatus();
      offLog();
      offConv();
    };
  }, [user, refreshRoster]);

  useEffect(() => {
    if (user) return;
    // Signed out: drop the socket so a stale token doesn't keep reconnecting.
    getSessionLive({ sessionId: DEFAULT_SESSION_ID, mapId: DEFAULT_MAP_ID }).stop();
  }, [user]);

  const toggleOpen = () => {
    setOpen((v) => {
      const next = !v;
      writeOpenPref(next);
      if (!next) setFormOpen(false);
      return next;
    });
  };

  const chooseFilter = (next: FeedFilter) => {
    setFilter(next);
    writeFilterPref(next);
  };

  const { entries, unread } = snapshot;
  // Derived views — cheap, but memoised so a status tick doesn't re-group.
  const visible = useMemo(() => filterFeedEntries(entries, filter), [entries, filter]);
  const latest = useMemo(() => latestPerCharacter(entries), [entries]);
  // The freshest roll overall — highlighted in the strip as it lands.
  const freshestId = latest[0]?.id ?? null;

  // Own characters first so the picker's default is almost always right.
  const viewerSub = (impersonating ? effectiveUser?.sub : user?.sub) ?? '';
  const orderedRoster = useMemo(() => {
    const own = roster.filter((c) => c.ownerSub === viewerSub);
    const rest = roster.filter((c) => c.ownerSub !== viewerSub);
    return [...own, ...rest];
  }, [roster, viewerSub]);

  if (!user) return null;

  if (!open) {
    return (
      <div className={`rollfeed rollfeed--collapsed${dockClass}`}>
        <button
          type="button"
          className="rollfeed__pill"
          onClick={toggleOpen}
          aria-label={unread > 0 ? `Open roll feed, ${unread} new rolls` : 'Open roll feed'}
          aria-expanded={false}
        >
          <Icon name="dice" size={18} />
          <span>Rolls</span>
          {unread > 0 && <Badge>{unread > 99 ? '99+' : unread}</Badge>}
        </button>
      </div>
    );
  }

  return (
    <div className={`rollfeed rollfeed--open${dockClass}`}>
      <section className="rollfeed__panel" aria-label="Roll feed">
        <header className="rollfeed__head">
          <Icon name="dice" size={18} className="rollfeed__head-icon" />
          <h2 className="rollfeed__title">Roll feed</h2>
          <span
            className={`rollfeed__status rollfeed__status--${status}`}
            title={status === 'open' ? 'Live' : status === 'reconnecting' ? 'Reconnecting…' : status}
            aria-label={status === 'open' ? 'Connected' : status}
          />
          <IconButton
            icon="add"
            size="sm"
            variant={formOpen ? 'default' : 'ghost'}
            label={formOpen ? 'Close the log-a-roll form' : 'Log an off-app roll'}
            aria-expanded={formOpen}
            onClick={() => setFormOpen((v) => !v)}
          />
          <IconButton icon="chevron" size="sm" variant="ghost" label="Collapse roll feed" onClick={toggleOpen} />
        </header>

        {formOpen && (
          <LogRollForm
            roster={orderedRoster}
            dm={dm}
            onDone={() => setFormOpen(false)}
          />
        )}

        {latest.length > 0 && (
          <section className="rollfeed__latest" aria-label="Latest roll per character">
            <div className="rollfeed__latest-head">
              <span className="rollfeed__latest-title">Latest per character</span>
              <div className="rollfeed__filter" role="group" aria-label="Show">
                <button
                  type="button"
                  className={`rollfeed__filter-btn${filter === 'all' ? ' rollfeed__filter-btn--on' : ''}`}
                  aria-pressed={filter === 'all'}
                  onClick={() => chooseFilter('all')}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`rollfeed__filter-btn${filter === 'rolls' ? ' rollfeed__filter-btn--on' : ''}`}
                  aria-pressed={filter === 'rolls'}
                  onClick={() => chooseFilter('rolls')}
                  title="Rolls only — hide session and scene events"
                >
                  Rolls
                </button>
              </div>
            </div>
            <ul className="rollfeed__latest-list">
              {latest.map((e) => (
                // Keyed by the ROLL id, not the character, so a new roll for
                // a character remounts the row and replays the landing flash.
                <li
                  key={e.id}
                  className={
                    'rollfeed__row rollfeed__row--latest' +
                    (e.id === freshestId ? ' rollfeed__row--fresh' : '') +
                    entryStateClass(e)
                  }
                  title={formatRollLine(e)}
                >
                  <RollLineView e={e} />
                </li>
              ))}
            </ul>
          </section>
        )}

        {visible.length === 0 ? (
          <p className="rollfeed__empty">
            {entries.length === 0 ? (
              <>
                No rolls yet. Sheet rolls land here as they happen; tap <strong>+</strong> to log a roll made
                on D&amp;D Beyond or real dice.
              </>
            ) : (
              <>Nothing to show for this filter yet.</>
            )}
          </p>
        ) : (
          <ol className="rollfeed__list" aria-live="polite" aria-relevant="additions">
            {visible.map((e) =>
              e.kind === 'system' ? (
                <SystemRow key={e.id} e={e} />
              ) : (
                <li key={e.id} className={'rollfeed__row rollfeed__row--roll' + entryStateClass(e)}>
                  <div className="rollfeed__line" title={formatRollLine(e)}>
                    <RollLineView e={e} />
                  </div>
                  <div className="rollfeed__detail">
                    <span className="rollfeed__dice">{formatDice(e)}</span>
                    {e.natural === 'nat20' && <span className="rollfeed__nat">NAT 20</span>}
                    {e.natural === 'nat1' && <span className="rollfeed__nat">NAT 1</span>}
                    {e.source === 'manual' && (
                      <span className="rollfeed__tag rollfeed__tag--manual" title="Logged by hand — rolled off-app">
                        off-app
                      </span>
                    )}
                    {e.whisper && (
                      <span className="rollfeed__tag rollfeed__tag--whisper" title="Whispered — only the roller and the DM can see this">
                        <Icon name="eye-off" size={11} /> whisper
                      </span>
                    )}
                    {e.characterName && e.characterName !== e.actorLabel && (
                      <span className="rollfeed__player">{e.actorLabel}</span>
                    )}
                    <time className="rollfeed__time" dateTime={e.ts}>{formatTime(e.ts)}</time>
                  </div>
                </li>
              ),
            )}
          </ol>
        )}
      </section>
    </div>
  );
}

/** "Session started" / "Switched active scene to …" — muted, one line. */
function SystemRow({ e }: { e: RollFeedSystemEntry }) {
  return (
    <li className="rollfeed__row rollfeed__row--system" title={`${e.summary} — ${e.actorLabel}`}>
      <span className="rollfeed__system-text">{e.summary}</span>
      <time className="rollfeed__time" dateTime={e.ts}>{formatTime(e.ts)}</time>
    </li>
  );
}

interface LogRollFormProps {
  roster: CharacterBundle[];
  dm: boolean;
  onDone: () => void;
}

/**
 * One-action off-app roll logger: character + what + natural + total, Enter
 * to post. Posts via the shared SessionLive socket so it's audited and
 * broadcast exactly like a sheet roll.
 */
function LogRollForm({ roster, dm, onDone }: LogRollFormProps) {
  const [characterId, setCharacterId] = useState<string>(() => roster[0]?.id ?? '');
  const [type, setType] = useState('');
  const [natural, setNatural] = useState('');
  const [total, setTotal] = useState('');
  const [whisper, setWhisper] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const typeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    typeRef.current?.focus();
  }, []);

  // If the roster arrives after the form opened, pick the first character.
  useEffect(() => {
    if (!characterId && roster[0]) setCharacterId(roster[0].id);
  }, [roster, characterId]);

  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    const character = roster.find((c) => c.id === characterId) ?? null;
    try {
      const req = buildManualRollRequest({
        type,
        total: total.trim() === '' ? Number.NaN : Number(total),
        natural: natural.trim() === '' ? null : Number(natural),
        characterId: character?.id ?? null,
        characterName: character ? characterLabel(character) : null,
        whisper,
      });
      getSessionLive({ sessionId: DEFAULT_SESSION_ID, mapId: DEFAULT_MAP_ID }).send(req);
      setError(null);
      setType('');
      setNatural('');
      setTotal('');
      onDone();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not log that roll';
      setError(msg);
      reportFrontendError(err, { source: 'roll-feed', action: 'log-manual-roll' }, 'warn');
    }
  };

  return (
    <form className="rollfeed__form" onSubmit={submit} aria-label="Log an off-app roll">
      <label className="rollfeed__field rollfeed__field--wide">
        <span className="rollfeed__label">Character</span>
        <select
          className="ui-select"
          value={characterId}
          onChange={(e) => setCharacterId(e.target.value)}
        >
          {roster.length === 0 && <option value="">(no character)</option>}
          {roster.map((c) => (
            <option key={c.id} value={c.id}>{characterLabel(c)}</option>
          ))}
          {roster.length > 0 && <option value="">(no character)</option>}
        </select>
      </label>
      <label className="rollfeed__field rollfeed__field--wide">
        <span className="rollfeed__label">What</span>
        <input
          ref={typeRef}
          className="ui-input"
          type="text"
          list="rollfeed-types"
          placeholder="Attack, Stealth check, Fireball damage…"
          value={type}
          onChange={(e) => setType(e.target.value)}
          maxLength={80}
          required
        />
        <datalist id="rollfeed-types">
          {MANUAL_ROLL_TYPES.map((t) => <option key={t} value={t} />)}
        </datalist>
      </label>
      <label className="rollfeed__field">
        <span className="rollfeed__label">Natural</span>
        <input
          className="ui-input"
          type="number"
          inputMode="numeric"
          min={1}
          max={20}
          placeholder="d20"
          value={natural}
          onChange={(e) => setNatural(e.target.value)}
        />
      </label>
      <label className="rollfeed__field">
        <span className="rollfeed__label">Total</span>
        <input
          className="ui-input"
          type="number"
          inputMode="numeric"
          placeholder="22"
          value={total}
          onChange={(e) => setTotal(e.target.value)}
          required
        />
      </label>
      <div className="rollfeed__form-actions">
        <label className="rollfeed__whisper" title="Only you and the DM will see it">
          <input type="checkbox" checked={whisper} onChange={(e) => setWhisper(e.target.checked)} />
          <Icon name="eye-off" size={13} /> {dm ? 'DM-only' : 'Whisper'}
        </label>
        <button type="button" className="ui-btn ui-btn--subtle ui-btn--sm" onClick={onDone}>Cancel</button>
        <button type="submit" className="ui-btn ui-btn--primary ui-btn--sm">Log roll</button>
      </div>
      {error && <p className="rollfeed__error" role="alert">{error}</p>}
    </form>
  );
}
